"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { nzd } from "@aluume/shared/lib/format";

type Product = { id: string; name: string; price_type: string; price: number; venue_slug: string | null; list_order: number; image_url: string | null };
type Step = "pick" | "personalise";

const labelCls = "text-[11px] uppercase tracking-[1.6px] text-ink-soft";
const inputCls = "w-full border-b border-ink/30 bg-transparent py-2 text-[15px] text-ink outline-none focus:border-ink";

function VoucherPreview({ product, amount, to, from, message }: {
  product: Product; amount: number; to: string; from: string; message: string;
}) {
  return (
    <div className="overflow-hidden rounded-2xl shadow-[0px_20px_48px_-16px_rgba(57,51,48,0.4)]">
      <div className="relative" style={{ aspectRatio: "533/800", backgroundColor: "#393331" }}>
        <img
          src="/email/voucher-bg.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          aria-hidden
        />
        {/* Scrim: light at top, heavy at bottom */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom,rgba(57,51,49,0.25) 0%,rgba(57,51,49,0.05) 30%,rgba(57,51,49,0.72) 65%,rgba(57,51,49,0.88) 100%)" }}
          aria-hidden
        />
        {/* Top label */}
        <div className="absolute left-7 right-7 top-7">
          <p style={{ fontFamily: "var(--font-sans)", fontSize: 11, letterSpacing: "3px", color: "rgba(57,51,49,0.75)" }}>
            ALUUME · GIFT VOUCHER
          </p>
        </div>
        {/* Bottom: product info + To/From/message */}
        <div className="absolute bottom-0 left-0 right-0 px-7 pb-7">
          <p style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 21, color: "#f7f2ea", lineHeight: 1.25 }}>
            {product.name}
          </p>
          <p className="mt-1" style={{ fontFamily: "var(--font-sans)", fontWeight: 300, fontSize: 13, color: "rgba(247,242,234,0.75)" }}>
            NZD {nzd(amount)}
          </p>
          <div className="mt-5 flex justify-between border-t border-white/20 pt-5">
            <div>
              <p style={{ fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "1.5px", color: "rgba(247,242,234,0.45)" }}>TO</p>
              <p style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 16, color: "#f7f2ea" }}>{to || "—"}</p>
            </div>
            <div className="text-right">
              <p style={{ fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "1.5px", color: "rgba(247,242,234,0.45)" }}>FROM</p>
              <p style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 16, color: "#f7f2ea" }}>{from || "—"}</p>
            </div>
          </div>
          {message ? (
            <p className="mt-3" style={{ fontFamily: "var(--font-sans)", fontWeight: 300, fontSize: 12, fontStyle: "italic", color: "rgba(247,242,234,0.8)", lineHeight: 1.55 }}>
              &ldquo;{message}&rdquo;
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function VoucherShop() {
  const router = useRouter();
  const [step, setStep] = useState<Step>("pick");
  const [products, setProducts] = useState<Product[]>([]);
  const [sel, setSel] = useState<Product | null>(null);
  const [amount, setAmount] = useState("");
  const [to, setTo] = useState("");
  const [from, setFrom] = useState("");
  const [message, setMessage] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");

  useEffect(() => {
    fetch("/api/vouchers/products").then(r => r.json()).then(j => setProducts(j.products ?? [])).catch(() => {});
  }, []);

  const effAmount = sel ? (sel.price_type === "editable" ? Number(amount || sel.price) : sel.price) : 0;

  function pick(p: Product) {
    setSel(p);
    setAmount(String(p.price));
    setStep("personalise");
  }

  function goToCheckout() {
    if (!sel) return;
    sessionStorage.setItem("aluume_voucher_draft", JSON.stringify({
      product_id: sel.id,
      product_name: sel.name,
      image_url: sel.image_url,
      amount: effAmount,
      to, from, message,
      recipient_email: recipientEmail || null,
    }));
    router.push("/checkout?type=voucher");
  }

  // ── Step 1: Pick ───────────────────────────────────────────────────────────
  if (step === "pick") {
    return (
      <div className="mx-auto max-w-[1100px] px-6 py-16">
        <p className="eyebrow mb-3">Aluume</p>
        <h2 style={{ fontFamily: "var(--font-display)" }} className="mb-3 text-[clamp(2rem,4vw,3rem)] font-light text-ink">
          The gift of an <span className="em">inspired pause.</span>
        </h2>
        <p className="mb-10 text-[17px] font-light leading-relaxed text-ink-soft">
          Redeemable across both Bathe and Thermae · valid for three years.
        </p>

        {products.length === 0 ? (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse rounded-2xl bg-ink/5" style={{ aspectRatio: "533/800" }} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {products.map((p) => (
              <button
                key={p.id}
                onClick={() => pick(p)}
                className="group flex flex-col overflow-hidden rounded-2xl bg-white text-left shadow-[0px_18px_40px_-16px_rgba(57,51,48,0.18)] transition-shadow hover:shadow-[0px_24px_48px_-12px_rgba(57,51,48,0.28)]"
              >
                <div className="w-full overflow-hidden" style={{ aspectRatio: "533/800" }}>
                  <img
                    src={p.image_url ?? "/email/voucher-bg.jpg"}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col justify-between gap-3 p-6">
                  <h3 style={{ fontFamily: "var(--font-display)" }} className="text-[19px] font-light leading-snug text-ink">
                    {p.name}
                  </h3>
                  <div className="flex items-end justify-between">
                    <p className="text-[14px] font-light text-ink-soft">
                      {p.price_type === "editable" ? `From NZD ${nzd(p.price)}` : `NZD ${nzd(p.price)}`}
                    </p>
                    <span className="ui-label text-ink-soft transition-colors group-hover:text-ink">Select →</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Step 2: Personalise ────────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-[1100px] px-6 py-12">
      <button onClick={() => setStep("pick")} className="ui-label mb-8 text-ink-soft hover:text-ink">← Back to vouchers</button>
      <div className="grid gap-12 lg:grid-cols-[1fr_380px]">
        <section className="flex flex-col gap-8">
          <div>
            <p className={`${labelCls} mb-1`}>Selected voucher</p>
            <h3 style={{ fontFamily: "var(--font-display)" }} className="text-[24px] font-light text-ink">{sel!.name}</h3>
          </div>

          {sel!.price_type === "editable" && (
            <label className="flex max-w-[200px] flex-col gap-1.5">
              <span className={labelCls}>Amount (NZD)</span>
              <input type="number" min={10} max={5000} value={amount} onChange={e => setAmount(e.target.value)} className={inputCls} />
            </label>
          )}

          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <label className="flex flex-col gap-1.5">
              <span className={labelCls}>To (on the card)</span>
              <input value={to} onChange={e => setTo(e.target.value)} placeholder="Mum" className={inputCls} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className={labelCls}>From (on the card)</span>
              <input value={from} onChange={e => setFrom(e.target.value)} placeholder="Sophie & Jack" className={inputCls} />
            </label>
            <label className="col-span-2 flex flex-col gap-1.5">
              <span className={labelCls}>Recipient&rsquo;s email <span className="normal-case tracking-normal opacity-60">(optional — we&rsquo;ll send the voucher directly to them)</span></span>
              <input type="email" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="mum@example.com" className={inputCls} />
            </label>
            <label className="col-span-2 flex flex-col gap-1.5">
              <span className={labelCls}>Message (on the card)</span>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} maxLength={220}
                placeholder="Happy birthday! Time to unwind…" className={`${inputCls} resize-none`} />
            </label>
          </div>

          <button
            onClick={goToCheckout}
            className="flex h-[54px] w-full items-center justify-center rounded-pill bg-ink text-[13px] uppercase tracking-[2px] text-ink-on-dark transition-colors hover:bg-accent sm:w-[280px]"
          >
            Continue to checkout
          </button>
        </section>

        <aside className="self-start lg:sticky lg:top-[100px]">
          <p className={`${labelCls} mb-3`}>Preview</p>
          <VoucherPreview product={sel!} amount={effAmount} to={to} from={from} message={message} />
        </aside>
      </div>
    </div>
  );
}
